import { useCallback, useEffect, useState, useMemo } from 'react';
import { publish, subscribe, unsubscribe } from '../../../features/EventsBus/services/service';
import { MeetDomainEvents } from '../../../features/Meet/domain/events';
import { useMeetContext } from '../../../features/Meet/providers/MeetContextProvider';
import { useMediaContext } from '../../../features/Media/providers/MediaContextProvider';
import { MediaDomainEvents } from '../../../features/Media/domain';
import { useVideoCallContext } from '../../../features/VideoCall/providers/VideoCallContextProvider';
import { VideoCallEvents } from '../../../features/VideoCall/domain/events';
import usePublisher from '../../../features/VideoCall/hooks/usePublisher';
import { useNavigate } from 'react-router-dom';

export function useRoom(code?: string, tenant?: string) {
	const navigate = useNavigate();
	const {
		sessionRef,
		createSession,
		joinSession,
		disconnectSession,
		participantJoined,
		stopSessionListeners,
		sendMuted,
		sendUnmuted
	} = useVideoCallContext();
	const { meet, connectionCredentials, retrieveMeet, sendProfessionalJoined, isProfessional } = useMeetContext();
	const { acceptedMediaPermissions, checkPermissions } = useMediaContext();
	const {
		publisher,
		publisherPublishing,
		publisherScreenSharing,
		hideOptionDisabled,
		muteOptionDisabled,
		hiddenCam,
		muted,
		createPublisher,
		cycleVideo,
		startScreenSharing,
		stopScreenSharing,
		toggleCamVisibility,
		toggleMute,
		destroyPublisher
	} = usePublisher();

	const [enlargedHostImage, setEnlargedHostImage] = useState(false);
	const [openedMeetActions, setOpenedMeetActions] = useState(false);
	const [hangUpDialog, setHangUpDialog] = useState(false);

	const openMeetActions = useCallback(() => {
		setOpenedMeetActions(true);
	}, []);

	const closeMeetActions = useCallback(() => {
		setOpenedMeetActions(false);
	}, []);

	const toggleHostImageSize = useCallback(() => {
		setEnlargedHostImage(!enlargedHostImage);
	}, [enlargedHostImage]);

	const initMeet = useCallback(() => {
		if (code) {
			retrieveMeet(code, tenant);
		}
	}, [code, tenant]);

	const readyToConnect = useMemo(() => {
		return meet?.status === 'STARTED' && acceptedMediaPermissions;
	}, [meet, acceptedMediaPermissions]);


	const createSessionWithCredentials = useCallback(() => {
		if (connectionCredentials) {
			createSession(connectionCredentials);
		}
	}, [connectionCredentials]);

	const toggleMic = useCallback((e: React.MouseEvent<Element, MouseEvent>) => {
		e.stopPropagation();
		if (muted) {
			sendUnmuted();
		} else {
			sendMuted();
		}
		toggleMute();
	}, [muted]);

	const toggleHangUpDialog = useCallback(() => {
		setHangUpDialog(!hangUpDialog);
	}, [hangUpDialog]);

	const patientHangUp = useCallback(() => {
		navigate(-1);
	}, []);

	const hangUp = useCallback(() => {
		if (isProfessional) {
			toggleHangUpDialog();
		} else {
			patientHangUp();
		}
	}, [isProfessional]);

	useEffect(() => {
		function retrieveMeetCallback() {
			if (meet) {
				publish(MeetDomainEvents.alreadyRetrievedMeet);
				return;
			} 

			initMeet();
		}

		subscribe(MeetDomainEvents.retrieveMeet, retrieveMeetCallback);

		return () => {
			unsubscribe(MeetDomainEvents.retrieveMeet, retrieveMeetCallback);
		};
	}, [meet]);

	useEffect(() => {
		subscribe(MeetDomainEvents.retrievedMeet, checkPermissions);
		subscribe(MeetDomainEvents.alreadyRetrievedMeet, checkPermissions);

		return () => {
			unsubscribe(MeetDomainEvents.retrievedMeet, checkPermissions);
			unsubscribe(MeetDomainEvents.alreadyRetrievedMeet, checkPermissions);
		};
	}, [acceptedMediaPermissions]);
	
	useEffect(() => {
		if (!meet) {
			return;
		}

		function acceptedMediaPermissionsCallback() {
			if (!sessionRef.current) {
				createSessionWithCredentials();
			}
		}

		subscribe(MediaDomainEvents.acceptedMediaPermissions, acceptedMediaPermissionsCallback);
		subscribe(MediaDomainEvents.alreadyAcceptedMediaPermissions, acceptedMediaPermissionsCallback);

		return () => {
			unsubscribe(MediaDomainEvents.acceptedMediaPermissions, acceptedMediaPermissionsCallback);
			unsubscribe(MediaDomainEvents.alreadyAcceptedMediaPermissions, acceptedMediaPermissionsCallback);
		};
	  }, [meet]);

	useEffect(() => {
		function sessionCreatedCallback() {
			stopSessionListeners();
		}

		subscribe(VideoCallEvents.sessionCreated, sessionCreatedCallback);

		return () => {
			unsubscribe(VideoCallEvents.sessionCreated, sessionCreatedCallback);
		};
	}, []);

	useEffect(() => {
		if (!meet) {
			return;
		}

		function sessionConnectedCallback() {
			createPublisher('meet-publisher');
			sendProfessionalJoined();
		}

		subscribe(VideoCallEvents.sessionConnected, sessionConnectedCallback);
		subscribe(VideoCallEvents.sessionAlreadyCreated, sessionConnectedCallback);

		return () => {
			unsubscribe(VideoCallEvents.sessionConnected, sessionConnectedCallback);
			unsubscribe(VideoCallEvents.sessionAlreadyCreated, sessionConnectedCallback);
		};
	  }, [meet]);

	useEffect(() => {
		function publisherCreatedCallback() {
			if (!publisher.current) {
				return;
			}
			joinSession(publisher.current);
		}

		subscribe(VideoCallEvents.publisherCreated, publisherCreatedCallback);

		return () => {
			unsubscribe(VideoCallEvents.publisherCreated, publisherCreatedCallback);
		};
	}, [publisher]);

	useEffect(() => {
		function sessionLostCallback() {
			destroyPublisher();
		}

		subscribe(VideoCallEvents.sessionLost, sessionLostCallback);

		return () => {
			unsubscribe(VideoCallEvents.sessionLost, sessionLostCallback);
		};
	}, []);

	useEffect(() => {
		function participantJoinedCallback(event: CustomEvent<Partial<Record<string, any>>>) {
			participantJoined(event.detail.event);
		}

		subscribe(VideoCallEvents.participantJoined, participantJoinedCallback);

		return () => {
			unsubscribe(VideoCallEvents.participantJoined, participantJoinedCallback);
		};
	}, []);

	const finished = useCallback(() => {
		destroyPublisher();
		const tenantParam = tenant ? `/tenant/${tenant}` : '';
		navigate(`/feedback/c/${code}${tenantParam}`, { replace: true });
	}, [code, tenant]);

	const forcedToFinish = useCallback(() => {
		destroyPublisher();
		const tenantParam = tenant ? `/tenant/${tenant}` : '';
		navigate(`/finish/c/${code}${tenantParam}`, { replace: true });
	}, [code, tenant]);

	useEffect(() => {
		publish(MeetDomainEvents.roomAccess);
		publish(MeetDomainEvents.retrieveMeet);
		subscribe(MeetDomainEvents.hangedUpAndFinished, finished);
		subscribe(MeetDomainEvents.forcedToFinish, forcedToFinish);

		return () => {
			unsubscribe(MeetDomainEvents.hangedUpAndFinished, finished);
			unsubscribe(MeetDomainEvents.forcedToFinish, forcedToFinish);
			disconnectSession();
		};
	}, []);

	return {
		enlargedHostImage,
		readyToConnect,
		publisherPublishing,
		publisherScreenSharing,
		openedMeetActions,
		hideOptionDisabled,
		muteOptionDisabled,
		hiddenCam,
		muted,
		hangUpDialog,
		toggleHostImageSize,
		cycleVideo,
		startScreenSharing,
		stopScreenSharing,
		openMeetActions,
		closeMeetActions,
		toggleCamVisibility,
		toggleMic,
		toggleHangUpDialog,
		hangUp,
	};
}